// Preselección de editadas por motivo — lógica PURA (motivos.test.ts). El backend
// clasifica con un modelo de TEXTO por qué el cliente editó cada imagen
// (`Caso.motivo_edicion`: fallo de la generación / cambio creativo / ambiguo); acá solo
// se filtra, se cuenta y se mezcla lo que llega. Sin React ni fetch.

import type { Caso, ClasificacionMotivos, MotivoConImagen, MotivoEdicion } from './tipos';

export type FiltroMotivo = MotivoEdicion | 'todos' | 'sin_clasificar';

export interface FiltroCasos {
  motivo: FiltroMotivo;
  texto: string;
}

export const SIN_FILTRO: FiltroCasos = { motivo: 'todos', texto: '' };

/**
 * Minúsculas, sin acentos ni diéresis, trimmeado: "Camión" y "camion" tienen que dar lo
 * mismo. NFD separa la letra de su diacrítico y se borra el diacrítico (U+0300–U+036F);
 * la ñ queda como n, que para buscar es lo cómodo ("nino" encuentra "niño").
 */
export function normalizarTexto(s: string | null | undefined): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** Todo el texto de un caso donde tiene sentido buscar: instrucciones, producto, variante, empresa y la razón de la IA. */
export function textoBuscable(caso: Caso): string {
  return normalizarTexto(
    [
      ...caso.ediciones.map((e) => e.instruction ?? ''),
      caso.producto.nombre ?? '',
      caso.producto.variante_nombre ?? '',
      caso.empresa.nombre ?? '',
      caso.motivo_edicion?.razon ?? '',
    ].join('\n'),
  );
}

export function pasaMotivo(caso: Caso, motivo: FiltroMotivo): boolean {
  if (motivo === 'todos') return true;
  if (motivo === 'sin_clasificar') return caso.motivo_edicion === null;
  return caso.motivo_edicion?.motivo === motivo;
}

/** ¿Hay algo filtrando? Un texto de solo espacios no cuenta. */
export function hayFiltro(f: FiltroCasos): boolean {
  return f.motivo !== 'todos' || normalizarTexto(f.texto) !== '';
}

/**
 * Filtro client-side: motivo Y texto. El texto se parte en palabras y TODAS tienen que
 * aparecer (en cualquier orden) en el texto buscable del caso, sin acentos ni mayúsculas:
 * "brazo mas" encuentra "Sacá el brazo de más".
 */
export function filtrarCasos(casos: Caso[], filtro: FiltroCasos): Caso[] {
  const palabras = normalizarTexto(filtro.texto).split(/\s+/).filter(Boolean);
  return casos.filter((c) => {
    if (!pasaMotivo(c, filtro.motivo)) return false;
    if (palabras.length === 0) return true;
    const t = textoBuscable(c);
    return palabras.every((p) => t.includes(p));
  });
}

/** Ids de las EDITADAS cuyo motivo está en la lista, en el orden del dataset. El control nunca entra: no se clasifica ni se preselecciona. */
export function idsPorMotivo(casos: Caso[], motivos: MotivoEdicion[]): string[] {
  const set = new Set(motivos);
  return casos
    .filter((c) => c.tipo_caso === 'editada' && c.motivo_edicion !== null && set.has(c.motivo_edicion.motivo))
    .map((c) => c.imagen_id);
}

export interface ResumenMotivos {
  fallo: number;
  ambiguo: number;
  creativo: number;
  sin_clasificar: number;
}

/** Conteo por motivo sobre las editadas (el control no se clasifica, no suma en ningún lado). */
export function resumenMotivos(casos: Caso[]): ResumenMotivos {
  const r: ResumenMotivos = { fallo: 0, ambiguo: 0, creativo: 0, sin_clasificar: 0 };
  for (const c of casos) {
    if (c.tipo_caso !== 'editada') continue;
    const m = c.motivo_edicion?.motivo;
    if (m === 'fallo_generacion') r.fallo += 1;
    else if (m === 'ambiguo') r.ambiguo += 1;
    else if (m === 'cambio_creativo') r.creativo += 1;
    else r.sin_clasificar += 1;
  }
  return r;
}

/**
 * Pisa `motivo_edicion` con lo que llegó del backend, por imagen. Inmutable: array nuevo,
 * objetos nuevos SOLO para los casos que cambiaron (los demás conservan identidad, así
 * React no re-renderiza tarjetas que no se tocaron). Motivos de ids que no están en
 * `casos` se ignoran; el `imagen_id` no se copia adentro del motivo.
 */
export function mezclarMotivos(casos: Caso[], motivos: MotivoConImagen[]): Caso[] {
  if (motivos.length === 0) return [...casos];
  const porImagen = new Map(motivos.map((m) => [m.imagen_id, m]));
  return casos.map((c) => {
    const m = porImagen.get(c.imagen_id);
    if (!m) return c;
    const { imagen_id: _imagen, ...motivo } = m;
    return { ...c, motivo_edicion: motivo };
  });
}

/** Terminada = `finished_at` seteado (puede traer `error` y aun así parte clasificada: "1 de 8 tandas fallaron"). */
export function clasificacionTerminada(c: ClasificacionMotivos): boolean {
  return c.finished_at !== null;
}
